import { lazy, Suspense, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Color, ColorDynamic, Inline } from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { Collections } from '@material-ui/icons';
import { useFlag } from 'shared/settings/FeatureToggles';
import { isInspectionPhotoSynced } from './inspectionPhotoSync';
import { InspectionPhotoPendingPlaceholder } from './InspectionPhotoPendingPlaceholder';
import { PhotoViewMode } from 'shared/ui/PhotoViewMode';

const PhotoGallery = lazy(() => import('shared/ui/PhotoGallery'));

export const VehicleImageContainer = styled.div`
  width: 120px;
  height: 96px;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  & [data-content-more-photos='true'] {
    background: rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    color: ${Color.White};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }
`;
const PhotoContainer = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
  background-color: ${ColorDynamic.Silver400};
`;

interface Props {
  photos: Array<{ original_photo_url: string; photo_thumbnail_url: string }>;
}

export function ViewLoadInspectionDamagePhotos({ photos }: Props) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(-1);
  // The slide the grid view was opened from; closing the grid reopens the
  // gallery there.
  const [photoGridSlideIndex, setPhotoGridSlideIndex] = useState(-1);
  const isPhotoSyncEnabled = useFlag('ctms_photo_sync');

  const slides = useMemo(
    () =>
      photos.map((photo) => ({
        src:
          photo.original_photo_url || !isPhotoSyncEnabled
            ? photo.original_photo_url
            : 'https://storage.googleapis.com/superdispatch-email-resources/photo-in-sync-placeholder.png',
      })),
    [photos, isPhotoSyncEnabled],
  );

  const gridPhotos = useMemo(
    () =>
      slides.map((slide, index) => ({
        id: `${index}:${slide.src}`,
        url: slide.src,
        thumbnail_url: slide.src,
        is_vehicle_detected: null,
        is_video: false,
      })),
    [slides],
  );

  const photosToDisplay = useMemo(() => photos.slice(0, 2), [photos]);

  if (photos.length === 0) {
    return null;
  }

  const handleImageClick = (index: number) => {
    setCurrentSlideIndex(index);
  };

  return (
    <>
      <Inline space="xsmall">
        {photosToDisplay.map((photo, index) => {
          const isSynced = isInspectionPhotoSynced({
            url: photo.original_photo_url,
            thumbnailUrl: photo.photo_thumbnail_url,
          });
          const showSyncedThumbnail = !isPhotoSyncEnabled || isSynced;

          return (
            <VehicleImageContainer
              key={index}
              role="button"
              aria-label="Open damage photo"
            >
              <PhotoContainer
                onClick={() => {
                  handleImageClick(index);
                }}
              >
                {showSyncedThumbnail ? (
                  <img
                    src={photo.photo_thumbnail_url}
                    alt={`Car Photo ${index + 1}`}
                  />
                ) : (
                  <InspectionPhotoPendingPlaceholder />
                )}
              </PhotoContainer>

              {index === 1 && photos.length > 2 && (
                <div
                  data-content-more-photos="true"
                  onClick={() => {
                    handleImageClick(index);
                  }}
                >
                  <TextBox variant="body-semibold" color="inherit">
                    +{photos.length - 2}
                  </TextBox>
                  <Collections color="inherit" />
                </div>
              )}
            </VehicleImageContainer>
          );
        })}
      </Inline>

      <Suspense fallback={null}>
        <PhotoGallery
          slides={slides}
          currentSlideIndex={currentSlideIndex}
          setCurrentSlideIndex={setCurrentSlideIndex}
          onClose={() => {
            setCurrentSlideIndex(-1);
          }}
          onModeChange={() => {
            setPhotoGridSlideIndex(currentSlideIndex);
          }}
        />
      </Suspense>

      <PhotoViewMode
        open={photoGridSlideIndex >= 0}
        inlinePhotos={gridPhotos}
        onClose={() => {
          setCurrentSlideIndex(photoGridSlideIndex);
          setPhotoGridSlideIndex(-1);
        }}
        onSelect={(index) => {
          setCurrentSlideIndex(index);
          setPhotoGridSlideIndex(-1);
        }}
      />
    </>
  );
}
